import { Component, computed, effect, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { NeverPublishedListingsResponse } from 'dashboard/components/visuals-dashboard/widgets/never-published-listings-widget/never-published-listings-data-response.interface';
import { IceDashboardDisplayStateEnum } from 'dashboard/enums/visuals/ice-dashboard-display-state.enum';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import {
    IceVisualsDashboardTableDisplayComponent,
    TableColumn,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-table-display/ice-visuals-dashboard-table-display.component';

interface ListingData {
    id: string;
    created: string;
    name: string;
}
@Component({
    selector: 'app-never-published-listings-widget',
    imports: [IceVisualsDashboardTableDisplayComponent],
    templateUrl: './never-published-listings-widget.component.html',
    styleUrl: './never-published-listings-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class NeverPublishedListingsWidgetComponent {
    widget = input.required<DashboardWidget>();
    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;
    iceDashboardDisplayStateEnum = IceDashboardDisplayStateEnum;

    onLangChange = toSignal(this.translateService.onLangChange);

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    totalListings = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return 0;
        }
        return widgetData.totalListings;
    });

    columns: TableColumn[] = [
        { key: 'id', translationKey: 'dashboard.visuals.widgets.neverPublishedListings.table.columns.id', width: '30%' },
        { key: 'created', translationKey: 'dashboard.visuals.widgets.neverPublishedListings.table.columns.created', width: '30%' },
        { key: 'name', translationKey: 'dashboard.visuals.widgets.neverPublishedListings.table.columns.name', width: '40%' },
    ];

    listings = computed<ListingData[]>(() => {
        const widgetData = this.dataService.data();
        this.onLangChange(); // Re-compute when language changes
        if (!widgetData) {
            return [];
        }
        const listingsArray: ListingData[] = [];
        widgetData.ranges.forEach((range) => {
            let createdRange = '';
            if (range.days === 1) {
                createdRange = this.translateService.instant('dashboard.visuals.components.table.dateFormat.daysAgo.singular', {
                    numberOfDays: range.days,
                });
            } else {
                createdRange = this.translateService.instant('dashboard.visuals.components.table.dateFormat.daysAgo.plural', {
                    numberOfDays: range.days,
                });
            }
            const item: ListingData = {
                id: range.mappedId,
                created: createdRange,
                name: range.title,
            };
            listingsArray.push(item);
        });
        return listingsArray;
    });

    widgetState = computed(() => {
        const widgetData = this.dataService.data();
        if (!widgetData) {
            return null;
        }
        return widgetData.totalListings === 0 ? IceDashboardDisplayStateEnum.SUCCESS : IceDashboardDisplayStateEnum.WARNING;
    });

    constructor(
        public dataService: VisualsDashboardDataService<NeverPublishedListingsResponse>,
        private visualsDashboardService: VisualsDashboardService,
        private translateService: TranslateService,
    ) {
        effect(() => {
            const widgetId = this.widgetId();
            this.dataService.load(widgetId);
        });
        effect(() => {
            const state = this.widgetState();
            const widgetHtmlId = this.widget().htmlId;
            if (widgetHtmlId && state !== null) {
                this.visualsDashboardService.setHtmlIdBackgroundStateOverride(widgetHtmlId, state);
            }
        });
    }
}
